import type {
  SheetComponent,
  SheetComponentProps,
  SheetInstance,
  SheetOpenOptions,
} from "./sheet-types";

type Listener = () => void;

let sheets: SheetInstance[] = [];
const listeners = new Set<Listener>();
const closingTimers = new Map<string, ReturnType<typeof setTimeout>>();
const CLOSE_ANIMATION_MS = 200;

function emitChange() {
  listeners.forEach((listener) => listener());
}

function createId() {
  return `sheet-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function findSheet(target: string) {
  return sheets.find((sheet) => sheet.id === target || sheet.key === target);
}

function removeSheet(id: string) {
  sheets = sheets.filter((sheet) => sheet.id !== id);
  closingTimers.delete(id);
  emitChange();
}

function finish(
  sheet: SheetInstance,
  settle: () => void,
) {
  if (sheet.status === "closing") {
    return;
  }

  sheets = sheets.map((current) =>
    current.id === sheet.id ? { ...current, status: "closing" } : current,
  );
  emitChange();

  const timer = setTimeout(() => {
    removeSheet(sheet.id);
    settle();
  }, CLOSE_ANIMATION_MS);
  closingTimers.set(sheet.id, timer);
}

function close(target?: string) {
  const sheet = target ? findSheet(target) : sheets.at(-1);

  if (sheet) {
    finish(sheet, () => sheet.resolveValue(null));
  }
}

function closeAll() {
  const snapshot = sheets;
  sheets = [];
  closingTimers.forEach((timer) => clearTimeout(timer));
  closingTimers.clear();
  emitChange();
  snapshot.forEach((sheet) => sheet.resolveValue(null));
}

function resolve(id: string, value: unknown) {
  const sheet = findSheet(id);

  if (!sheet) {
    return;
  }

  finish(sheet, () => sheet.resolveValue(value));
}

function reject(id: string, reason?: unknown) {
  const sheet = findSheet(id);

  if (!sheet) {
    return;
  }

  finish(sheet, () => sheet.rejectValue(reason));
}

function open<TProps extends object, TResult = void>(
  component: SheetComponent<TProps, TResult>,
  props: Omit<TProps, keyof SheetComponentProps<TResult>>,
  options: SheetOpenOptions = {},
) {
  if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  if (options.key) {
    close(options.key);
  }

  const id = createId();

  return new Promise<TResult | null>((resolveValue, rejectValue) => {
    sheets = [
      ...sheets,
      {
        id,
        status: "open",
        side: "right",
        closeOnEsc: true,
        closeOnOutside: true,
        ...options,
        component: component as SheetComponent<Record<string, unknown>, unknown>,
        props: props as Record<string, unknown>,
        resolveValue: (value: unknown) =>
          resolveValue((value ?? null) as TResult | null),
        rejectValue,
      },
    ];
    emitChange();
  });
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getState() {
  return sheets;
}

export const sheetManager = {
  close,
  closeAll,
  getState,
  open,
  reject,
  resolve,
  subscribe,
};
